import { useState } from 'react'
import * as Icons from 'lucide-react'

// 50 professional travel/content related icons
export const CATEGORY_ICONS = [
    // Travel & Transportation
    'Map', 'MapPin', 'Compass', 'Navigation', 'Globe', 'Plane', 'Car', 'Bike', 'Ship',
    // Nature & Outdoors
    'Sun', 'Sunrise', 'Sunset', 'Mountain', 'TreePalm', 'Waves', 'Cloud', 'Umbrella',
    // Activities
    'Camera', 'Video', 'Music', 'Gamepad', 'Trophy', 'Target', 'Sparkles',
    // Food & Dining
    'UtensilsCrossed', 'Coffee', 'Wine', 'IceCream', 'Pizza', 'Soup',
    // Accommodation & Services
    'Hotel', 'Home', 'Building', 'Store', 'Ticket', 'CreditCard', 'Wallet',
    // Information & Media
    'BookOpen', 'Newspaper', 'FileText', 'MessageSquare', 'Info', 'HelpCircle',
    // Categories & Organization
    'Tag', 'Bookmark', 'Star', 'Heart', 'ThumbsUp', 'Award', 'Medal',
    // Misc
    'Lightbulb', 'Zap', 'Clock', 'Calendar', 'Gift'
] as const

export type IconName = typeof CATEGORY_ICONS[number]

interface IconPickerProps {
    value: string
    onChange: (icon: string) => void
    label?: string
}

export default function IconPicker({ value, onChange, label }: IconPickerProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')

    const filteredIcons = CATEGORY_ICONS.filter(icon =>
        icon.toLowerCase().includes(search.toLowerCase())
    )

    const getIconComponent = (iconName: string) => {
        const IconComponent = (Icons as any)[iconName]
        return IconComponent ? <IconComponent className="w-5 h-5" /> : null
    }

    const CurrentIcon = (Icons as any)[value]

    return (
        <div className="relative">
            {label && (
                <label className="block text-sm font-medium mb-1">{label}</label>
            )}

            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-2 border rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500"
            >
                <div className="flex items-center gap-2">
                    {CurrentIcon ? (
                        <CurrentIcon className="w-5 h-5 text-gray-700" />
                    ) : (
                        <Icons.HelpCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">{value || 'Select icon'}</span>
                </div>
                <Icons.ChevronDown className={`w-4 h-4 text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full max-w-sm bg-white border rounded-lg shadow-lg">
                    <div className="p-2 border-b">
                        <div className="relative">
                            <Icons.Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search icons..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-primary-500"
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-2 grid grid-cols-6 gap-1">
                        {filteredIcons.map(iconName => {
                            const isSelected = value === iconName
                            return (
                                <button
                                    key={iconName}
                                    type="button"
                                    onClick={() => {
                                        onChange(iconName)
                                        setIsOpen(false)
                                        setSearch('')
                                    }}
                                    className={`p-2 rounded hover:bg-gray-100 flex items-center justify-center ${isSelected ? 'bg-primary-100 text-primary-600 ring-1 ring-primary-500' : 'text-gray-600'
                                        }`}
                                    title={iconName}
                                >
                                    {getIconComponent(iconName)}
                                </button>
                            )
                        })}
                    </div>
                    {filteredIcons.length === 0 && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            No icons found
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// Helper component to render category icon by name
export function CategoryIcon({ name, className = 'w-5 h-5' }: { name: string; className?: string }) {
    const IconComponent = (Icons as any)[name]

    // Fallback for emoji icons (legacy)
    if (!IconComponent) {
        // Check if it's an emoji
        if (/\p{Emoji}/u.test(name)) {
            return <span className="text-lg">{name}</span>
        }
        return <Icons.Folder className={className} />
    }

    return <IconComponent className={className} />
}
